import React from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { Row, Col } from '../../Grid';
import editIcon from '../../../svg/edit.svg';
import cancelIcon from '../../../svg/cancel.svg';
import posted from '../../../svg/posted.svg';
import EnterReservation from './EnterReservation';
import CancelReservation from './CancelReservation';
import OfferDateBox from './OfferDateBox';
import { correctTime, stateColor, stateText } from '../OfferSlots/ReservationSlot';
import { formatTime } from '../../../helpers';

// Types

interface Props {
  list: { offer: Offer; attendee: Attendee }[];
  type: string;
  date: string;
  title: string;
}

// Styles

const Header = styled.div`
  position: relative;
  background-color: #fafafa;
  padding: 10px 10px 20px 10px;
  color: #555555;
  font-size: 22px;
  padding-right: 60px;
  min-height: 70px;
`;

const Title = styled.div`
  position: relative;
  background-color: #fafafa;
  padding: 10px 10px 20px 10px;
  color: #555555;
  font-size: 22px;
  padding-right: 60px;
`;

const AppointmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0 !important;
  background: #fff;
  border-radius: 8px;
`;

const AppointmentItem = styled.li`
  padding: 0;
`;

const StyledRow = styled(Row)`
  display: flex;
  align-items: center;
  background-color: var(--color-beige);
  border-bottom: 1px solid white;
  padding: 5px 0px 4px 20px;
  min-width: 0;
`;

const TimeCol = styled(Col)`
  flex: 0 0 60px;

  font-family: BrandonGrotesque-BoldItalic;
  font-size: 14px;
  font-style: italic;
  padding: 6px 10px 3px 0px;
  color: var(--color-khaki);
`;

const TitleCol = styled(Col)`
  flex: 1;
  min-width: 0;
  display: block;
  font-size: 14px;
  font-style: italic;
  color: var(--color-greyish-brown);
  margin-right: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: BrandonGrotesque-RegularItalic;
  color: #555555;
`;

const AttendeeCol = styled(Col)`
  flex: 0 0 190px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px 0px;
  img.avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    margin-right: 5px;
    object-fit: cover;
  }
`;

const Name = styled.span`
  font-size: 14px;
  color: #333;
  text-decoration: none;
  display: flex;
  align-items: center;
  line-height: 18px;
  font-family: BrandonGrotesque-Regular;
`;

const ButtonsCol = styled(Col)`
  flex: 0 0 200px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 0px 15px 0px 0;

  button {
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }

    img {
      width: 16px;
      height: 16px;
    }
  }
`;

const EnterReservationButton = styled.button`
  background-color: var(--color-red) !important;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 3px 16px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const AttendeeLink = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 5px;
  color: #333333;
`;

const Posted = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
`;

const ModelStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: right;
  font-family: BrandonGrotesque-BoldItalic;
  font-size: 14px;
  font-style: italic;
  line-height: 0.86;
  text-align: right;
  font-weight: bold;
  color: ${(props) => props.color};
  line-height: 46px;
`;

const ActionButtons = ({
  attendee,
  offer,
  type,
}: {
  offer: Offer;
  attendee: Attendee;
  type: string;
}) => {
  if (offer.state === 'past' || !(attendee.status === 'incomplete' || attendee.status === 'active'))
    return null;

  const reserved_for = attendee.reserved_for ? moment(attendee.reserved_for).format('h:mm A') : '';

  return (
    <>
      <EnterReservation type={type} offer={offer} attendee={attendee}>
        {!!reserved_for ? (
          <button aria-label="Edit reservation">
            <img src={editIcon} alt="Edit" />
          </button>
        ) : (
          <EnterReservationButton>Enter Reservation</EnterReservationButton>
        )}
      </EnterReservation>

      {!!reserved_for && (
        <CancelReservation type={type} offer={offer} attendee={attendee}>
          <button aria-label="Cancel reservation">
            <img src={cancelIcon} alt="Cancel" />
          </button>
        </CancelReservation>
      )}
    </>
  );
};

function OfferReservationView({ list, type, date, title }: Props): JSX.Element | null {
  return (
    <>
      {title === 'Awaiting reservations' ? (
        <Title>{title}</Title>
      ) : (
        <Header>
          <OfferDateBox date={date} />
        </Header>
      )}

      <AppointmentList>
        {list.map(({ attendee, offer }, idx) => {
          const attendeeStatus = attendee.status;
          const offerSubtitle = offer.subtitle;
          const modelName = attendee.model.name;
          const modelAvatar = attendee.model?.avatar || 'https://via.placeholder.com/30';
          const reserved_for = attendee.reserved_for
            ? formatTime(attendee.reserved_for, true, offer.timezone)
            : '';
          const instagram = attendee.model?.instagram?.profile_url || '#';

          return (
            <AppointmentItem key={attendee.id || idx}>
              <StyledRow>
                {attendeeStatus !== 'active' ||
                  (!!reserved_for && <TimeCol>{reserved_for}</TimeCol>)}
                <TitleCol>{offerSubtitle || 'Untitled Offer'}</TitleCol>
                <AttendeeCol>
                  <AttendeeLink href={instagram} target="_blank" rel="noopener noreferrer">
                    <img className="avatar" src={modelAvatar} alt="avatar" />
                    <Name>{modelName}</Name>
                  </AttendeeLink>
                </AttendeeCol>
                <ButtonsCol>
                  <ActionButtons offer={offer} attendee={attendee} type={type} />
                  <ModelStatus color={stateColor[attendee.status]}>
                    {stateText[attendee.status]} {correctTime(date, attendee)}
                    {attendee.posted_at && (
                      <Posted>
                        <span>{attendee.posts_count}</span>
                        <img width="16px" height="16px" src={posted} alt="" />
                      </Posted>
                    )}
                  </ModelStatus>
                </ButtonsCol>
              </StyledRow>
            </AppointmentItem>
          );
        })}
      </AppointmentList>
    </>
  );
}

export default connect(null, {})(OfferReservationView);
